/**
 * «Посеять без плана» — мутация поверх `POST /api/v1/fields/{id}/sow`.
 *
 * Одна ручка = одна транзакция на сервере: посев (и, если сев уже прошёл,
 * факт-операция «Сев») либо появляются вместе, либо не появляются вовсе.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fieldCardKeys } from '../api';
import { operationKeys } from '@/components/operations/operationsApi';
import type { toSowBody } from './sowMath';

export type SowBody = ReturnType<typeof toSowBody>;

export interface SowResult {
  sowing: { id: string; status: string | null };
  operation_id: string | null;
  seed_need_kg: number | null;
  summary: string;
}

export function useSowWithoutPlan(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<SowResult, Error, SowBody>({
    mutationFn: (body) => api.post<SowResult>(`/api/v1/fields/${fieldId}/sow`, body),
    onSuccess: () => {
      // Занятие сезона, история культур и список операций поля читают ровно те
      // строки, которые ручка только что создала — иначе соседние блоки
      // карточки покажут «посевов нет» сразу после успешного сохранения.
      void qc.invalidateQueries({ queryKey: ['field-card', 'occupancy', fieldId] });
      void qc.invalidateQueries({ queryKey: fieldCardKeys.cropHistory(fieldId) });
      void qc.invalidateQueries({ queryKey: operationKeys.ofField(fieldId) });
      void qc.invalidateQueries({ queryKey: operationKeys.list() });
    },
  });
}
